import loadableComponent from "@loadable/component";
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
// import { Breadcrumbs } from "./Component/Breadcrumbs";
import { CommandPalette } from "./Component/CommandPalette";
import { Breadcrumbs } from "./Component/Breadcrumbs";
import ErrorBoundary from "./Component/ErrorBoundary";
import { FooterNew } from "./Component/FooterNew";
import { HelpWidget } from "./Component/HelpWidget";
import { MobileBottomNav } from "./Component/MobileBottomNav";
import PrivateRoute from "./Component/PrivateRoute";
import { Toaster } from "./Component/Toaster";
import { TooltipProvider } from "./Component/ui/tooltip";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider as VestnThemeProvider } from "./contexts/ThemeContext";
import { WatchlistProvider } from "./contexts/WatchlistContext";
import { createPortal } from "react-dom";
import Gs from "./Theme/globalStyles";
import Loader from "./Component/Loader/Loder";
import Header from "./Component/header";

// Portal: renders Loader on document.body so it escapes PageTransition's opacity animation.
// Prevents the two backdrop-filter layers (Loader + Header) from forcing GPU re-compositing.
const PortalLoader = () => createPortal(<Loader />, document.body);

const loadable: typeof loadableComponent = ((factory: any, options: any = {}) =>
  loadableComponent(factory, { fallback: <PortalLoader />, ...options })) as any;

const Platform = loadable(() => import("./Pages/platform"));
const MarketPlace = loadable(() => import("./Pages/marketplace"));
const SecondaryMarketplace = loadable(
  () => import("./Pages/secondaryMarketplace")
);
const Rental = loadable(() => import("./Pages/rental"));
const HowToUse = loadable(() => import("./Pages/how-to-use"));
const About = loadable(() => import("./Pages/about"));
const Profile = loadable(() => import("./Pages/profile"));
const EditProfile = loadable(() => import("./Pages/profile-edit"));
const NftDetail = loadable(() => import("./Pages/primaryPropertyDetails"));
const PropertyDetails = loadable(() => import("./Pages/propertyDetails"));
const RentalDetails = loadable(() => import("./Pages/rentalDetails"));
const Collections = loadable(() => import("./Pages/collections"));
const CollectionDetail = loadable(() => import("./Pages/collection-detail"));
const CollectionEdit = loadable(() => import("./Pages/collection-edit"));
const CreatorProfile = loadable(() => import("./Pages/creator-profile"));
const Calculator = loadable(() => import("./Pages/calculator"));
const CookiePolicy = loadable(() => import("./Pages/cookies"));
const Privacy = loadable(() => import("./Pages/privacy"));
const Terms = loadable(() => import("./Pages/terms"));
const Legal = loadable(() => import("./Pages/legal"));
const Faq = loadable(() => import("./Pages/faq"));
const BlogList = loadable(() => import("./Pages/blog-list"));
const MeetTheTeam = loadable(() => import("./Pages/LandingPage/MeetTheTeam"));
const TermSection = loadable(() => import("./Pages/LandingPage/TermSection"));
const ProfileCollected = loadable(() => import("./Pages/profile-collected"));
const ProfileLiked = loadable(() => import("./Pages/profile-liked"));
const ProfileRentalEarnings = loadable(
  () => import("./Pages/profile-rental-earnings")
);
const ProfileTransactions = loadable(
  () => import("./Pages/profile-transactions")
);
const ProfileAnalytics = loadable(() => import("./Pages/profile-analytics"));
const ProfileSettings = loadable(() => import("./Pages/profile-settings"));
// Careers, Press, Partners redirect to home – pages commented out
// const Press = loadable(() => import('./Pages/press'));
// const Partners = loadable(() => import('./Pages/partners'));
const Contact = loadable(() => import("./Pages/contact"));
const RiskDisclosure = loadable(() => import("./Pages/risk-disclosure"));
const RegulatoryCompliance = loadable(
  () => import("./Pages/regulatory-compliance")
);

// Layout renders once at App level — Header/Footer never remount on navigation.
const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isHomePage =
    location.pathname === "/" || location.pathname === "/platform";
  const hideGlobalBreadcrumbs =
    location.pathname === "/privacy" ||
    location.pathname === "/terms" ||
    location.pathname === "/cookies";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main
        className={`flex-1 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] bg-background ${
          isHomePage ? "pt-4 lg:pt-4" : "pt-24 lg:pt-24"
        }`}
      >
        {!hideGlobalBreadcrumbs && <Breadcrumbs />}
        {children}
      </main>
      <FooterNew />
    </div>
  );
};

// RouteWrapper no longer wraps with Layout — Layout lives at App level.
const RouteWrapper = ({ component: Component, ...rest }: any) => (
  <Component {...rest} />
);

// Animated Routes Component
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <Switch location={location}>
        {/* Platform is the home page, served at the root route */}
        <Route
          exact
          path="/"
          render={(props) => <RouteWrapper component={Platform} {...props} />}
        />

        {/* Public Routes */}
        {/* Legacy /platform path redirects to root to keep existing links working */}
        <Route path="/platform" render={() => <Redirect to="/" />} />
        <Route
          path="/marketplace"
          render={(props) => (
            <RouteWrapper component={MarketPlace} {...props} />
          )}
        />
        {/* Redirect legacy marketplace routes to canonical routes */}
        <Route
          path="/primarymarketplace"
          render={() => <Redirect to="/marketplace" />}
        />
        <Route
          path="/secondary"
          render={() => <Redirect to="/secondarymarketplace" />}
        />
        <Route
          path="/secondary-marketplace"
          render={() => <Redirect to="/secondarymarketplace" />}
        />
        <Route
          path="/secondarymarketplace"
          render={(props) => (
            <RouteWrapper component={SecondaryMarketplace} {...props} />
          )}
        />
        <Route
          exact
          path="/rental"
          render={(props) => <RouteWrapper component={Rental} {...props} />}
        />
        <Route
          path="/how-to-use"
          render={(props) => <RouteWrapper component={HowToUse} {...props} />}
        />
        <Route
          path="/about"
          render={(props) => <RouteWrapper component={About} {...props} />}
        />
        <Route
          path="/legal"
          render={(props) => <RouteWrapper component={Legal} {...props} />}
        />
        <Route
          path="/faq"
          render={(props) => <RouteWrapper component={Faq} {...props} />}
        />
        <Route
          path="/cookies"
          render={(props) => (
            <RouteWrapper component={CookiePolicy} {...props} />
          )}
        />
        <Route
          path="/privacy"
          render={(props) => <RouteWrapper component={Privacy} {...props} />}
        />
        <Route
          path="/terms"
          render={(props) => <RouteWrapper component={Terms} {...props} />}
        />
        <Route
          path="/calculator"
          render={(props) => <RouteWrapper component={Calculator} {...props} />}
        />
        <Route
          path="/collections"
          render={(props) => (
            <RouteWrapper component={Collections} {...props} />
          )}
        />
        <Route
          path="/blog-list"
          render={(props) => <RouteWrapper component={BlogList} {...props} />}
        />
        <Route
          path="/meet-the-team"
          render={(props) => (
            <RouteWrapper component={MeetTheTeam} {...props} />
          )}
        />
        <Route
          path="/terms-and-conditions"
          render={(props) => (
            <RouteWrapper component={TermSection} {...props} />
          )}
        />
        {/* Careers, Press, Partners – redirect to home; routes commented out */}
        {/* <Route
                    path='/press'
                    render={(props) => (
                        <RouteWrapper component={Press} {...props} />
                    )}
                />
                <Route
                    path='/partners'
                    render={(props) => (
                        <RouteWrapper component={Partners} {...props} />
                    )}
                /> */}
        <Route
          path="/contact"
          render={(props) => <RouteWrapper component={Contact} {...props} />}
        />
        <Route
          path="/risk-disclosure"
          render={(props) => (
            <RouteWrapper component={RiskDisclosure} {...props} />
          )}
        />
        <Route
          path="/regulatory-compliance"
          render={(props) => (
            <RouteWrapper component={RegulatoryCompliance} {...props} />
          )}
        />

        {/* Dynamic Routes */}
        <Route
          path="/primaryPropertyDetails/:id"
          render={(props) => <RouteWrapper component={NftDetail} {...props} />}
        />
        <Route
          path="/propertyDetails/:id"
          render={(props) => (
            <RouteWrapper component={PropertyDetails} {...props} />
          )}
        />
        <Route
          path="/rental/property/:id"
          render={(props) => (
            <RouteWrapper component={RentalDetails} {...props} />
          )}
        />
        <Route
          path="/collection-detail/:id"
          render={(props) => (
            <RouteWrapper component={CollectionDetail} {...props} />
          )}
        />
        <Route
          path="/creator/:id"
          render={(props) => (
            <RouteWrapper component={CreatorProfile} {...props} />
          )}
        />

        {/* Protected Routes */}
        {/* More specific routes must come before less specific ones */}
        <PrivateRoute
          path="/user/profile/collected"
          component={ProfileCollected}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/user/profile/liked"
          component={ProfileLiked}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/user/profile/rental-earnings"
          component={ProfileRentalEarnings}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/user/profile/transactions"
          component={ProfileTransactions}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/user/profile/analytics"
          component={ProfileAnalytics}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/user/profile/settings"
          component={ProfileSettings}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          exact
          path="/user/profile/full"
          component={Profile}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          exact
          path="/user/profile"
          component={Profile}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/profile"
          component={Profile}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/edit-profile"
          component={EditProfile}
          routeWrapper={RouteWrapper}
        />
        <PrivateRoute
          path="/collection-edit/:id"
          component={CollectionEdit}
          routeWrapper={RouteWrapper}
        />
        <Route path="/user" exact render={() => <Redirect to="/platform" />} />

        {/* 404 Route */}
        <Route
          render={() => (
            <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col items-center justify-center">
              <h1 className="text-6xl font-bold text-foreground mb-4">404</h1>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Page Not Found</h2>
              <p className="text-muted-foreground mb-8 max-w-md">
                The page you're looking for doesn't exist or has been moved.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/platform" className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors">
                  Go to Platform
                </a>
                <a href="/marketplace" className="px-6 py-3 bg-card border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors">
                  Browse Marketplace
                </a>
              </div>
              <div className="mt-12 text-sm text-muted-foreground">
                <p className="mb-2">Popular pages:</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="/about" className="hover:text-primary">About Us</a>
                  <a href="/how-to-use" className="hover:text-primary">How to Use</a>
                  <a href="/faq" className="hover:text-primary">FAQ</a>
                  <a href="/rental" className="hover:text-primary">Yield</a>
                </div>
              </div>
            </div>
          )}
        />
      </Switch>
      <MobileBottomNav />
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <VestnThemeProvider defaultTheme="dark">
      <TooltipProvider delayDuration={300} skipDelayDuration={0}>
        <WatchlistProvider>
          <NotificationProvider>
            <Toaster />
            <BrowserRouter>
              <ErrorBoundary>
                <Gs.GlobalStyle />
                <CommandPalette />
                <Layout>
                  <AnimatedRoutes />
                </Layout>
                <HelpWidget />
              </ErrorBoundary>
            </BrowserRouter>
          </NotificationProvider>
        </WatchlistProvider>
      </TooltipProvider>
    </VestnThemeProvider>
  </ErrorBoundary>
);

export default App;
